import * as React from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import Spinner from 'components/spinner/Spinner';

import DiscoveryContentResultsImage from './image/DiscoveryContentResultsImage';
import DiscoveryContentResultsContext from './context/DiscoveryContentResultsContext';

import { __t, applyTranslations } from 'utils/language';
import { buildImage } from 'utils/image';

import './discovery-content-results.scss';

import type { DiscoveryContentLoading, DiscoveryContentResult } from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

type Props = {
  userChannelConsents: ConsentReducerConsentsMap,
  results: Array<DiscoveryContentResult>,
  isFullScreen: boolean,
  isLoading: DiscoveryContentLoading,
  searchTerms: Array<string>,
  onGalleryModalLastPage: () => void
};

applyTranslations(require('./discovery-content-results.i18n.json'));

const msgKey = 'discovery_content_results';

class DiscoveryContentResults extends React.PureComponent<Props> {
  renderSpinner(): React.ReactNode {
    return (
      <div className="discovery-content-results__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderEmpty(): React.ReactNode {
    const { searchTerms } = this.props;

    return (
      <div className="discovery-content-results__empty">
        {__t(isEmpty(searchTerms) ? `${msgKey}.messages.no_search_terms` : `${msgKey}.messages.empty`)}
      </div>
    );
  }

  renderResult = (item: DiscoveryContentResult, index: number): JSX.Element => {
    const { isFullScreen, onGalleryModalLastPage, userChannelConsents } = this.props;
    const key = `${item.code}-${item.username}-${item.date}-${String(item.engagementRate)}-${index}`;

    const updatedItem = {
      ...item,
      ...buildImage(item)
    };

    return (
      <div
        key={`discovery-content-results__result-${key}`}
        className="discovery-content-results__result"
      >
        <DiscoveryContentResultsImage
          userChannelConsents={userChannelConsents}
          item={updatedItem}
          index={index}
          isFullScreen={isFullScreen}
          onGalleryModalLastPage={onGalleryModalLastPage}
        />
      </div>
    );
  };

  renderResults(): React.ReactNode {
    const { results, searchTerms, isFullScreen } = this.props;

    if (isEmpty(results) || isEmpty(searchTerms)) {
      return this.renderEmpty();
    }

    const resultsClasses = classnames('discovery-content-results__results', {
      'discovery-content-results__results--is-fullscreen': isFullScreen
    });

    return (
      <DiscoveryContentResultsContext.Provider value={results}>
        <div className={resultsClasses}>
          {
            (results || []).map(this.renderResult)
          }
        </div>
      </DiscoveryContentResultsContext.Provider>
    );
  }

  render(): React.ReactNode {
    const { isLoading } = this.props;

    return (
      <div className="discovery-content-results">
        {
          isLoading.results.standard
            ? this.renderSpinner()
            : this.renderResults()
        }
      </div>
    );
  }
}

export default DiscoveryContentResults;
